import styled from 'styled-components'
import { tokens } from 'styling/tokens'

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: none;
`

export const IconBtn = styled.button`
  width: 2rem;
  height: 2rem;
  display: grid;
  place-items: center;
  flex: none;
  border-radius: ${tokens.radius.control};
  background: ${tokens.surface.inset};
  border: 1px solid ${tokens.border.hairline};
  color: ${tokens.text.secondary};
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease;

  svg {
    width: 1rem;
    height: 1rem;
  }

  &:hover {
    color: ${tokens.accent.base};
    border-color: ${tokens.accent.base};
    background: ${tokens.accent.soft};
  }
`

export const RoomList = styled.div`
  border-top: 1px solid ${tokens.border.hairline};
  padding: 0.625rem 1rem 0.875rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

export const RoomGroup = styled.div`
  border: 1px solid ${tokens.border.hairline};
  border-radius: ${tokens.radius.control};
  background: ${tokens.surface.inset};
  padding: 0.75rem 0.875rem;
`

export const RoomHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.625rem;
`

export const RoomTitle = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  min-width: 0;
  flex-wrap: wrap;
`

export const RoomName = styled.span`
  font-size: ${tokens.font.body};
  font-weight: 600;
  color: ${tokens.text.primary};
`

export const RoomContext = styled.span`
  font-size: ${tokens.font.micro};
  color: ${tokens.text.muted};
`

export const RoomCount = styled.span`
  font-size: ${tokens.font.micro};
  font-weight: 600;
  color: ${tokens.text.secondary};
  white-space: nowrap;
  flex: none;
`

export const BedChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`

export const BedChip = styled.div<{ $accent: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.25rem 0.55rem;
  border-radius: ${tokens.radius.chip};
  background: ${tokens.surface.raised};
  border: 1px solid ${tokens.border.hairline};
  border-left: 2px solid ${(props) => props.$accent};
  font-size: ${tokens.font.micro};
`

export const BedChipDot = styled.span<{ $color: string }>`
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 50%;
  flex: none;
  background: ${(props) => props.$color};
`

export const BedChipLabel = styled.span`
  font-weight: 600;
  color: ${tokens.text.primary};
`

export const BedChipState = styled.span`
  color: ${tokens.text.muted};
`

export const RoomEmpty = styled.div`
  font-size: ${tokens.font.micro};
  color: ${tokens.text.muted};
`

export const BedChipButton = styled.button<{ $accent: string; $clickable?: boolean }>`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
  padding: 0.3rem 0.55rem;
  border-radius: ${tokens.radius.chip};
  background: ${tokens.surface.raised};
  border: 1px solid ${tokens.border.hairline};
  border-left: 2px solid ${(props) => props.$accent};
  font-size: ${tokens.font.micro};
  text-align: left;
  cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    ${(props) =>
      props.$clickable &&
      `border-color: ${tokens.accent.base}; background: ${tokens.accent.soft};`}
  }
`

export const BedChipTop = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
`

export const BedChipOccupant = styled.span<{ $overdue?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: ${tokens.font.micro};
  color: ${(props) => (props.$overdue ? tokens.status.danger.fg : tokens.text.secondary)};
  max-width: 12rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const OverdueDot = styled.span`
  width: 0.4rem;
  height: 0.4rem;
  border-radius: 50%;
  flex: none;
  background: ${tokens.status.danger.fg};
  box-shadow: 0 0 6px ${tokens.status.danger.fg};
`

export const DrawerScrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999998;
  background: rgba(10, 10, 12, 0.55);
  backdrop-filter: blur(2px);
`

export const DrawerPanel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: min(420px, 92vw);
  z-index: 999999;
  background: ${tokens.surface.base};
  border-left: 1px solid ${tokens.border.strong};
  box-shadow: ${tokens.shadow.card};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

export const DrawerHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.5rem;
  border-bottom: 1px solid ${tokens.border.hairline};
`

export const DrawerTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
`

export const DrawerBedLabel = styled.span`
  font-size: ${tokens.font.h1};
  font-weight: 600;
  color: ${tokens.text.primary};
`

export const DrawerLocation = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: ${tokens.font.label};
  color: ${tokens.text.muted};

  svg {
    width: 0.85rem;
    height: 0.85rem;
    flex: none;
  }
`

export const DrawerClose = styled.button`
  width: 2rem;
  height: 2rem;
  display: grid;
  place-items: center;
  flex: none;
  border-radius: ${tokens.radius.control};
  background: ${tokens.surface.inset};
  border: 1px solid ${tokens.border.hairline};
  color: ${tokens.text.secondary};
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;

  &:hover {
    color: ${tokens.accent.base};
    border-color: ${tokens.accent.base};
  }
`

export const DrawerBody = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  overflow-y: auto;
  flex: 1;
`

export const DrawerField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

export const DrawerFieldLabel = styled.span`
  font-size: ${tokens.font.micro};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${tokens.text.muted};
`

export const DrawerFieldValue = styled.span<{ $danger?: boolean }>`
  font-size: ${tokens.font.body};
  font-weight: 500;
  color: ${(props) => (props.$danger ? tokens.status.danger.fg : tokens.text.primary)};
`

export const DrawerFieldRow = styled.div`
  display: flex;
  gap: 2rem;
`

export const DrawerActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid ${tokens.border.hairline};

  button {
    width: 100%;
  }
`
